import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TrendingUp, TrendingDown, RefreshCw, AlertCircle, Euro } from "lucide-react";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

function eur(n) { return `${(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`; }

export default function StaffFinancials() {
  const [mrr, setMrr] = useState(null);
  const [ts, setTs] = useState(null);
  const [churn, setChurn] = useState(null);
  const [failed, setFailed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [start, setStart] = useState(new Date(Date.now() - 365 * 86400e3).toISOString().slice(0, 7));
  const [end, setEnd] = useState(new Date().toISOString().slice(0, 7));
  const [revenue, setRevenue] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [m, t, c, f] = await Promise.all([
        api.get("/staff/mrr"),
        api.get("/staff/mrr/timeseries?months=12"),
        api.get("/staff/churn?months=6"),
        api.get("/staff/failed-payments?limit=50"),
      ]);
      setMrr(m.data); setTs(t.data); setChurn(c.data); setFailed(f.data.failed_payments || []);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runRevenue = async () => {
    try {
      const r = await api.get(`/staff/revenue?start_month=${start}&end_month=${end}`);
      setRevenue(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const retryOne = async (id) => {
    try {
      await api.post("/staff/failed-payments/retry", { stripe_invoice_id: id });
      toast.success("Retry lancé"); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const bulkRetry = async () => {
    setBulkLoading(true);
    try {
      const r = await api.post("/staff/failed-payments/bulk-retry", { max_age_days: 7 });
      toast.success(`Bulk retry — ${r.data.retried_count} OK · ${r.data.errors_count} erreur(s)`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBulkLoading(false); }
  };

  if (loading) return <div className="text-slate-400 p-6">Chargement…</div>;
  return (
    <div className="space-y-6" data-testid="staff-financials">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="text-xs uppercase text-slate-500">MRR total</div>
            <div className="text-2xl font-bold text-emerald-400 mt-1" data-testid="mrr-total">{eur(mrr?.total_mrr_eur)}</div>
            <div className="text-[10px] text-slate-500">{mrr?.total_active_subscriptions} abonnements actifs</div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="text-xs uppercase text-slate-500">Churn (6m)</div>
            <div className="text-2xl font-bold text-red-400 mt-1" data-testid="churn-rate">{churn?.churn_rate_pct}%</div>
            <div className="text-[10px] text-slate-500">{churn?.total_canceled} résiliations</div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="text-xs uppercase text-slate-500">Failed payments</div>
            <div className="text-2xl font-bold text-amber-400 mt-1" data-testid="failed-count">{failed.length}</div>
            <div className="text-[10px] text-slate-500">à traiter</div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="text-xs uppercase text-slate-500">Revenu 12m</div>
            <div className="text-2xl font-bold text-blue-400 mt-1">{eur(ts?.total_period_eur)}</div>
            <div className="text-[10px] text-slate-500">facturé (paid)</div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader><CardTitle className="text-sm text-slate-200">MRR — évolution 12 mois</CardTitle></CardHeader>
        <CardContent className="h-56">
          <ResponsiveContainer>
            <LineChart data={ts?.months || []}>
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} />
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155" }} />
              <Line type="monotone" dataKey="revenue_eur" stroke="#22c55e" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-sm text-slate-200">Paiements en échec</CardTitle>
          <Button size="sm" onClick={bulkRetry} disabled={bulkLoading || failed.length === 0}
            data-testid="bulk-retry-btn" className="bg-amber-600 hover:bg-amber-700">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${bulkLoading ? "animate-spin" : ""}`} />
            Bulk retry (7 derniers jours)
          </Button>
        </CardHeader>
        <CardContent>
          {failed.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">Aucun paiement en échec</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800"><TableHead className="text-slate-400">Client</TableHead><TableHead className="text-slate-400">Montant</TableHead><TableHead className="text-slate-400">Tentatives</TableHead><TableHead className="text-slate-400">Action</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {failed.map(f => (
                  <TableRow key={f.stripe_invoice_id} className="border-slate-800">
                    <TableCell className="text-slate-200 text-sm">{f.tenant_name}</TableCell>
                    <TableCell className="text-red-300">{eur(f.amount_due_eur)}</TableCell>
                    <TableCell className="text-slate-400 text-xs">{f.attempt_count}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" onClick={() => retryOne(f.stripe_invoice_id)}
                        data-testid={`retry-${f.stripe_invoice_id}`}
                        className="border-slate-700 text-slate-200 bg-transparent hover:bg-slate-800">Retry</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader><CardTitle className="text-sm text-slate-200">Revenu par période</CardTitle></CardHeader>
        <CardContent>
          <div className="flex gap-2 items-end mb-3">
            <div><label className="text-xs text-slate-400">Début</label><Input type="month" value={start} onChange={e => setStart(e.target.value)} className="bg-slate-950 border-slate-700 text-slate-200 w-40" data-testid="revenue-start" /></div>
            <div><label className="text-xs text-slate-400">Fin</label><Input type="month" value={end} onChange={e => setEnd(e.target.value)} className="bg-slate-950 border-slate-700 text-slate-200 w-40" data-testid="revenue-end" /></div>
            <Button size="sm" onClick={runRevenue} data-testid="run-revenue">Calculer</Button>
          </div>
          {revenue && (
            <div className="text-sm text-slate-200">
              <p><b className="text-blue-400">{eur(revenue.total_revenue_eur)}</b> encaissés · {revenue.invoices_count} factures</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {Object.entries(revenue.by_plan || {}).map(([p, v]) => (
                  <Badge key={p} variant="outline" className="text-xs">{p}: {eur(v)}</Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
